import React from "react";
import ICON from "../icons.js";
import HomeImage from "./HomeImage.js";
import { stripJuristic } from "../../../components/text-sanitize.js";
import { STATUS_COLOR, money, hasValue, stripPrefixWord } from "../../../components/sale-format.js";

/* ============================================================================
 * HomePropertyCard — การ์ดห้อง 1 ใบ
 *
 * ป้ายมุมซ้ายบนบอกสถานะจริงของห้อง (ว่าง / จอง / ขายแล้ว) ไม่ใช่ป้ายโฆษณา
 * ผู้ใช้จะได้ไม่เสียเวลากดห้องที่ปิดดีลไปแล้ว
 *
 * ★ ไม่มีชื่อ/เบอร์เจ้าของห้องบนการ์ดเด็ดขาด — API สาธารณะไม่ส่งมาให้อยู่แล้ว
 *   ติดต่อได้ผ่านนิติบุคคลเท่านั้น
 *
 * ★ เคารพสวิตช์ "การตั้งค่าการขาย (โครงการ)" ที่ backend แนบมากับการ์ด (item.display)
 *   ปิดราคาขายไว้ = การ์ดใน /overview และ "ทรัพย์ใกล้เคียง" ต้องไม่โชว์ราคาขายด้วย
 *   ไม่มี display / ไม่มีคีย์นั้น = เปิด (backend รุ่นเก่าไม่ได้ส่งมา ต้องแสดงเหมือนเดิม)
 *
 * ★ กดทั้งใบได้ด้วยคีย์บอร์ด (Tab มาที่การ์ด แล้ว Enter/Space) — ปุ่มท้ายการ์ดยังกดแยกได้ตามปกติ
 * ========================================================================== */
export default function HomePropertyCard({ item, onOpen, onInquiry }) {
    const display = item.display || {};
    const show = (key) => display[key] !== false;

    const isRent = item.listing_type === "เช่า";
    const showBoth = item.listing_type === "ขายและเช่า";
    const status = isRent ? item.rent_status : item.selling_status;

    const showSale = !isRent && show("price_sale");
    const showRent = (isRent || showBoth) && show("price_rent");

    // สเปก : ประเภท / ห้องนอน / ห้องน้ำ / พื้นที่ / อาคาร / ชั้น — ขึ้นเฉพาะที่มีข้อมูลและสวิตช์เปิด
    const area = item.usable_area || item.room_size;
    const specs = [
        !!item.property_type && { key: "type", icon: ICON.home, text: item.property_type },
        show("spec_bedroom") && hasValue(item.bedroom_count) && { key: "bed", icon: ICON.bed, text: `${item.bedroom_count} นอน` },
        show("spec_bathroom") && hasValue(item.bathroom_count) && { key: "bath", icon: ICON.bath, text: `${item.bathroom_count} น้ำ` },
        show("spec_area") && hasValue(area) && { key: "area", icon: ICON.area, text: `${area} ตร.ม.` },
        show("spec_building") && !!item.building && { key: "building", icon: ICON.building, text: `อาคาร ${stripPrefixWord(item.building, "อาคาร")}` },
        show("spec_floor") && !!item.floor && { key: "floor", icon: ICON.floor, text: `ชั้น ${stripPrefixWord(item.floor, "ชั้น")}` },
    ].filter(Boolean);

    const open = () => onOpen && onOpen(item);
    const onKeyDown = (event) => {
        // เฉพาะตอนโฟกัสอยู่ที่ตัวการ์ดเอง — กด Enter บนปุ่มข้างในต้องทำงานของปุ่มนั้น
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            open();
        }
    };

    return (
        <article
            className="hm-card"
            role="link"
            tabIndex={0}
            aria-label={`ห้อง ${item.address_number} ${stripJuristic(item.project_name) || ""}`.trim()}
            onClick={open}
            onKeyDown={onKeyDown}
        >
            <div className="hm-card__media">
                {/* ไม่มีรูป หรือลิงก์รูปเสีย = ขึ้นภาพสำรองแทนไอคอนรูปแตกของเบราว์เซอร์ */}
                <HomeImage src={item.cover_image} alt={`ห้อง ${item.address_number}`} icon={ICON.home} />
                <div className="hm-card__badges">
                    {!!status && <span className={`hm-badge hm-badge--${STATUS_COLOR[status] || "green"}`}>{status}</span>}
                    {/* ขายพร้อมผู้เช่า = ผู้ซื้อเข้าอยู่เองไม่ได้ทันที ต้องเห็นตั้งแต่การ์ด */}
                    {item.sell_with_tenant && <span className="hm-badge hm-badge--orange">ขายพร้อมผู้เช่า</span>}
                </div>
            </div>

            <div className="hm-card__body">
                <div className="hm-card__head">
                    <div className="hm-card__head-text">
                        <h3 className="hm-card__title">ห้อง {item.address_number}</h3>
                        <div className="hm-card__loc">{stripJuristic(item.project_name) || ""}</div>
                    </div>
                </div>

                {!!specs.length && (
                    <div className="hm-card__specs">
                        {specs.map((spec) => (
                            <span key={spec.key} className="hm-card__spec">
                                <span className="hm-ico">{spec.icon}</span>
                                {spec.text}
                            </span>
                        ))}
                    </div>
                )}

                {/* ราคา — ประกาศที่ทั้งขายและเช่า แสดง 2 บรรทัด · ปิดทั้งคู่ = ไม่มีแถบราคา */}
                {(showSale || showRent) && (
                    <div className="hm-card__price">
                        {showSale ? (
                            <>
                                {`฿${money(item.price)}`}
                                {showRent && <small style={{ display: "block" }}>เช่า ฿{money(item.rent_price)} / เดือน</small>}
                            </>
                        ) : (
                            <>
                                {`฿${money(item.rent_price)}`}
                                <span>/ เดือน</span>
                            </>
                        )}
                    </div>
                )}

                {!!item.highlight && <p className="hm-card__note">{stripJuristic(item.highlight)}</p>}
            </div>

            <div className="hm-card__foot" onClick={(e) => e.stopPropagation()}>
                <span className="hm-card__views" aria-label={`เข้าชม ${money(item.view_count)} ครั้ง`}>
                    <span className="hm-ico" aria-hidden="true">{ICON.eye}</span> {money(item.view_count)}
                </span>
                <button type="button" className="hm-btn hm-btn--ghost hm-btn--sm" onClick={open}>
                    รายละเอียด
                </button>
                <button type="button" className="hm-btn hm-btn--primary hm-btn--sm" onClick={() => onInquiry && onInquiry(item)}>
                    นัดดูห้อง
                </button>
            </div>
        </article>
    );
}
